import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Select, Skeleton, Space, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { CreateRequisitionRequest, RequisitionRow, RequisitionStatus } from '@velora/contracts';
import { ApiError, productApi } from '../../api';

const STATUS_TONE: Record<RequisitionStatus, string> = {
  draft: 'default', submitted: 'processing', approved: 'success', rejected: 'error',
  ordered: 'blue', received: 'geekblue', paid: 'green', closed: 'default',
};

/**
 * Teacher — my requests. Same pipeline as admin `/expenses/requisitions`,
 * filtered to my own submissions. Approvals happen on the admin side.
 */
export function TeacherRequestsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['my-requisitions', institution],
    queryFn: () => productApi.listMyRequisitions(institution),
    enabled: !!institution,
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 4 }} />;
  const profile = q.data;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>My requests</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {profile.total} submission{profile.total === 1 ? '' : 's'}
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>New request</Button>
      </div>

      {profile.rows.length === 0 ? (
        <Empty description="No requests yet." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {profile.rows.map((r) => <RequisitionCard key={r.id} r={r} />)}
        </div>
      )}

      <NewRequestModal open={open} institution={institution} onClose={() => setOpen(false)} />
    </Space>
  );
}

function RequisitionCard({ r }: { r: RequisitionRow }) {
  return (
    <Card size="small">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <b>{r.code}</b>
        <Tag>{r.category}</Tag>
        <div style={{ flex: 1 }} />
        <Tag color={STATUS_TONE[r.status]}>{r.status}</Tag>
      </div>
      <div style={{ marginTop: 6, fontWeight: 600 }}>{r.subject}</div>
      <div style={{ fontSize: 12, color: '#5b6478', marginTop: 2 }}>
        estimate ₹{(r.amount_estimate_paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        {r.quote_count > 0 && ` · ${r.quote_count} quote${r.quote_count === 1 ? '' : 's'}`}
      </div>
      {r.rejected_reason && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#a24040' }}><b>Rejected:</b> {r.rejected_reason}</div>
      )}
      {r.note && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#5b6478' }}>{r.note}</div>
      )}
    </Card>
  );
}

interface Form1 {
  subject: string;
  category: 'stationery' | 'maintenance' | 'it' | 'books' | 'consumables' | 'travel' | 'other';
  amount_rupees: number;
  note?: string;
}

function NewRequestModal({
  open, institution, onClose,
}: { open: boolean; institution: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [form] = Form.useForm<Form1>();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => productApi.getMe(),
    enabled: open,
  });
  const create = useMutation({
    mutationFn: (input: CreateRequisitionRequest) => productApi.createRequisition(institution, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-requisitions', institution] });
      form.resetFields();
      void message.success('Submitted');
      onClose();
    },
  });
  const err = create.error instanceof ApiError
    ? ((create.error.body as { message?: string } | null)?.message ?? null)
    : (create.error instanceof Error ? create.error.message : null);

  return (
    <Modal
      open={open}
      title="New request"
      onCancel={() => { create.reset(); onClose(); }}
      okText="Submit"
      confirmLoading={create.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ category: 'stationery' as Form1['category'] }}
        onFinish={(v) =>
          create.mutate({
            subject: v.subject.trim(),
            category: v.category,
            requestor_name: me.data?.person.name ?? 'unknown',
            amount_estimate_paise: Math.round(v.amount_rupees * 100),
            note: v.note?.trim() || undefined,
          })
        }
      >
        <Form.Item name="subject" label="What do you need?" rules={[{ required: true, min: 1, max: 160 }]}>
          <Input placeholder="e.g. Chart paper for wall display" />
        </Form.Item>
        <Form.Item name="category" label="Category" rules={[{ required: true }]}>
          <Select options={(['stationery', 'maintenance', 'it', 'books', 'consumables', 'travel', 'other'] as const).map((c) => ({ value: c, label: c }))} />
        </Form.Item>
        <Form.Item name="amount_rupees" label="Estimate (₹)" rules={[{ required: true, type: 'number', min: 0 }]}>
          <InputNumber style={{ width: '100%' }} min={0} step={100} />
        </Form.Item>
        <Form.Item name="note" label="Note (optional)">
          <Input.TextArea rows={3} placeholder="Anything the admin should know before approving?" />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}
